import React, { useEffect, useRef } from 'react';
import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Button as MuiButton,
  Tooltip,
} from '@material-ui/core';
import Badge from '@material-ui/core/Badge';
import { withStyles } from '@material-ui/core/styles';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import CreateIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import WarningIcon from '@material-ui/icons/Warning';
import { observer } from 'mobx-react-lite';
import PeopleIcon from '@material-ui/icons/People';
import CloseIcon from '@material-ui/icons/Close';
import EventLabel from '../../components/EventLabel/EventLabel';
const StyledBadge = withStyles(theme => ({
  badge: {
    right: -3,
    top: 13,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
  },
}))(Badge);

const FieldErrorLine = ({ f }) => {
  if (f.reason === 'unvalidated') {
    return <EventLabel name="PageNotValidated" groupName="Form.Validation" />;
  }
  if (f.decorator) {
    return <EventLabel name={'RequiredField' + f.decorator} groupName="Form.Validation" />;
  }
  if (f.id?.toString().includes('INVOICE_')) {
    return <EventLabel name={'Invoice' + f.label} groupName="Form.Validation" />;
  }
  if (f.label === 'FileUploaderValidation') {
    return <EventLabel name={'FILE_UPLOADER' + f.reason} groupName="Form.Validation" />;
  }
  if (f.label === 'HotelValidation') {
    return <EventLabel name={'HOTEL_MODULE' + f.reason} groupName="Form.Validation" />;
  }
  if (f.fieldName) {
    return (
      <span>
        <strong>{f.fieldName}:</strong>{' '}
        {f.errorMessage || <EventLabel name={(f.type || 'FIELD') + f.reason} groupName="Form.Validation" />}
      </span>
    );
  }
  return <EventLabel name={'FIELD' + (f.reason || 'required')} groupName="Form.Validation" />;
};

const FGOC = ({ orders, setActiveOrder, removeOrder, activePersonNo, setLeader, orderValidationStates, openOrderCart, setOpenOrderCart }) => {
  const [open, setOpen] = React.useState(false);
  const [animateIn, setAnimateIn] = React.useState(false);
  const [pulse, setPulse] = React.useState(false);
  const wasVisibleRef = useRef(false);
  const prevOrdersLengthRef = useRef(orders.length);
  const theme = useTheme();

  React.useEffect(() => {
    if (openOrderCart) {
      setOpen(true);
      setOpenOrderCart(false);
    }
  }, [openOrderCart]);
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isVisible = orders.length >= 2;

  // Detect first appearance — delay until ConfirmationSnackbar morph completes
  useEffect(() => {
    if (isVisible && !wasVisibleRef.current) {
      wasVisibleRef.current = true;
      // Delay matches the ConfirmationSnackbar morph completion (~2.8s)
      const timer = setTimeout(() => setAnimateIn(true), 2800);
      return () => clearTimeout(timer);
    } else if (isVisible && wasVisibleRef.current) {
      setAnimateIn(true);
    }
  }, [isVisible]);

  // Detect subsequent adds — pulse the badge
  useEffect(() => {
    if (orders.length > prevOrdersLengthRef.current && wasVisibleRef.current && animateIn) {
      setPulse(true);
      const timer = setTimeout(() => setPulse(false), 800);
      prevOrdersLengthRef.current = orders.length;
      return () => clearTimeout(timer);
    }
    prevOrdersLengthRef.current = orders.length;
  }, [orders.length, animateIn]);

  const handleToggle = () => {
    setOpen(prev => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          right: 10,
          bottom: 10,
          zIndex: 120,
          display: isVisible && animateIn ? 'initial' : 'none',
          transform: pulse ? 'scale(1.25)' : 'scale(1)',
          transition: 'transform 0.3s ease-out',
        }}
      >
        <IconButton style={{ backgroundColor: '#fff' }} className="floating-cart-btn" onClick={handleToggle}>
          <StyledBadge badgeContent={orders.length || 1} color="error" overlap="rectangular">
            <PeopleIcon />
          </StyledBadge>
        </IconButton>
      </div>
      {open ? (
        <div className="backdrop" onClick={handleClose}>
          <div
            className={'aside-list' + (open ? ' show' : ' hidden')}
            style={{ display: 'flex' }}
            onClick={e => e.stopPropagation()}
          >
            <div>
              <IconButton style={{ padding: 0, margin: '15px 0px 0px 7px' }} onClick={handleClose}>
                <CloseIcon />
              </IconButton>{' '}
            </div>
            <div className="w-full">
              <DialogTitle id="responsive-dialog-title" style={{ padding: '10px 16px 0px 16px' }}>
                <EventLabel name={'MyOrders'} groupName={'Cms.GroupModule'} />
              </DialogTitle>
              <DialogContent style={{ padding: '0px 15px', overflowY: 'auto', maxHeight: '70vh' }} className="jir-dialog-content">
                <DialogContentText>
                  <List
                    component="nav"
                    aria-labelledby="nested-list-subheader"
                    subheader={
                      <ListSubheader component="div" style={{ padding: 0 }} id="nested-list-subheader">
                        <EventLabel name={'EditOrDeletePersons'} groupName={'Cms.GroupModule'} />
                      </ListSubheader>
                    }
                  >
                    {orders.map((o, i) => {
                      return (
                        <ListItem key={i} style={{ paddingLeft: 0 }}>
                          <ListItemIcon>
                            <AccountCircleIcon className={i == activePersonNo ? 'cms-active' : ''} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              (() => {
                                const orderState = orderValidationStates?.[o.orderId];
                                const name = o.person
                                  ? (o?.person?.firstName ?? '') + ' ' + (o?.person?.lastName ?? '')
                                  : null;
                                return (
                                  <span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      {orderState?.hasErrors && (
                                        <WarningIcon style={{ color: '#f57c00', fontSize: 18, flexShrink: 0 }} />
                                      )}
                                      <span style={{ color: orderState?.hasErrors ? '#d32f2f' : undefined }}>
                                        {name ?? <EventLabel name={'PersonNameIsNotFilledWarn'} groupName={'Cms.GroupModule'} />}
                                      </span>
                                    </span>
                                    {orderState?.hasErrors && orderState.invalidFields.length > 0 && (
                                      <div style={{ marginLeft: 22, marginTop: 2 }}>
                                        <span style={{ color: '#d32f2f', fontSize: 11, fontWeight: 600 }}>
                                          <EventLabel name={'OrderValidationErrorsTitle'} groupName={'Cms.GroupModule'} />
                                        </span>
                                        <ul style={{ margin: '1px 0 0 0', padding: 0, listStyle: 'disc', color: '#d32f2f', fontSize: 11 }}>
                                          {orderState.invalidFields.map((f, fi) => (
                                            <li key={fi}><FieldErrorLine f={f} /></li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </span>
                                );
                              })()
                            }
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title={<EventLabel name={'EditPersonTooltip'} groupName={'Cms.GroupModule'} />}>
                              <span>
                                <IconButton
                                  onClick={() => {
                                    setActiveOrder(i);
                                    handleClose();
                                  }}
                                >
                                  <CreateIcon />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {orders.length > 1 ? (
                              <IconButton
                                style={{
                                  opacity: o.leader == true || (o.orderId == o.orderGroupId && orders.filter(o => o.leader == true).length == 0) ? 1 : 0.5,
                                  padding: 0,
                                }}
                                onClick={() => {
                                  orders[i].invoice = orders.filter(a => a.leader)[0].invoice;
                                  orders[i].orderExtra = orders.filter(a => a.leader)[0].orderExtra;
                                  setLeader(i);
                                  orders.filter(a => !a.leader).map(x => (x.invoice = {}));
                                  orders.filter(a => !a.leader).map(x => (x.orderExtra = []));
                                }}
                              >
                                <Tooltip
                                  title={
                                    o.leader == true ? (
                                      <EventLabel name={'CurrentGroupLeaderTooltip'} groupName={'Cms.GroupModule'} />
                                    ) : (
                                      <EventLabel name={'MakeGroupLeaderTooltip'} groupName={'Cms.GroupModule'} />
                                    )
                                  }
                                >
                                  <VerifiedUserIcon />
                                </Tooltip>
                              </IconButton>
                            ) : null}
                            {o.orderId < 1 && (
                              <Tooltip
                                title={
                                  o.leader == true ? (
                                    <EventLabel name={'DeleteLeaderTooltipWarn'} groupName={'Cms.GroupModule'} />
                                  ) : (
                                    <EventLabel name={'DeletePersonTooltip'} groupName={'Cms.GroupModule'} />
                                  )
                                }
                              >
                                <span>
                                  <IconButton disabled={orders.length === 1 || o.leader === true} onClick={() => removeOrder(i)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </DialogContentText>
              </DialogContent>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export const FloatingGroupOrderCart = observer(FGOC);
